import { useId, type ReactNode } from 'react';
import { Badge } from './Chip';
import { StatusChip, type StatusChipTone, type StatusChipWeight } from './StatusChip';

// ------------------------------------------------------------------
// VersionPanel — P10 §2.4 design-refinement composite, story 2-140.
// "controlled cross-module version history: current, draft, published,
// approved, superseded, archived facts plus caller-supplied actions.
// Replaces the RailPanel-wrapped VersionHistory."
//
// ── Why this owns its own card, unlike the RailPanel it replaces ──────────
// `RailPanel` is content-agnostic chrome (title + a `children` slot); the
// report module used to wrap it around `VersionHistory`, a second, headless
// component that supplied only the row list. The table's own wording —
// "REPLACES the RailPanel-wrapped VersionHistory" — means the PAIR is
// replaced by ONE primitive: VersionPanel owns its own card surface and
// heading (same visual weight RailPanel + VersionHistory produced together),
// so a caller composes it directly rather than wrapping it in anything else.
// Wrapping VersionPanel in RailPanel (or `.glass-card`) again would be
// exactly the nested-card composition this story exists to remove.
//
// ── Why the status vocabulary is generic, not report-shaped ───────────────
// `ReportVersion['status']` today is only draft/approved/superseded — a
// narrower set than this component's contract line names. Templates carry
// a DIFFERENT, overlapping lifecycle (draft/in_review/published/archived,
// see `TemplateCard.tsx`'s `STATUS_META`). Per the StatusChip/StatusStepper
// precedent already written into this same P10 §2.4 table ("the primitive
// holds no module-specific transition rules — modules pass their states
// in"), `VersionPanel` does not import either module's label/tone map: the
// caller resolves its own status to this closed set plus a display label,
// and the panel owns only the TONE for each of the five values.
//
// ── Consumes StatusChip (story 2-141), the moment it existed ──────────────
// This file used to hand-roll its OWN tone map on top of `Badge` — a second
// status→tone map with the exact same shape as `TemplateCard`'s,
// `InspectionLifecycleBar`'s and `ReviewDetailPage`'s, which is precisely the
// near-duplication story 2-141 exists to end (P18 §6's "rule of three": once
// a shape repeats a third/fourth time, the next one consumes the primitive
// rather than re-deriving it). `STATUS_TONE`/`STATUS_WEIGHT` below now type
// as `StatusChipTone`/`StatusChipWeight` and are handed straight to
// `StatusChip`; the five-value closed set (`draft/published/approved/
// superseded/archived`) — this file's own contribution — is unchanged, only
// what RENDERS each row's tone moved from a second Badge map to the shared
// primitive.
//
// ── One real consumer today ────────────────────────────────────────────────
// `ReportDetailPage` is the only caller at HEAD — grepped tree-wide before
// writing this file. The generic prop shape is deliberate anticipatory
// design per the table's own "cross-module" framing, not proof of a second
// consumer that exists yet; say so plainly rather than pretend otherwise.
export type VersionPanelStatus = 'draft' | 'published' | 'approved' | 'superseded' | 'archived';

const STATUS_TONE: Record<VersionPanelStatus, StatusChipTone> = {
  draft: 'primary',
  published: 'success',
  approved: 'success',
  superseded: 'neutral',
  archived: 'neutral',
};

// Archived reads BOLD (not a second color) so it stays visually distinct from
// the unremarkable "superseded" neutral — same weight-as-fourth-axis move
// `TemplateCard.tsx`'s own `ORIGIN_META` comment already documents.
const STATUS_WEIGHT: Partial<Record<VersionPanelStatus, StatusChipWeight>> = {
  archived: 'bold',
};

export interface VersionPanelRow {
  /** Stable row identity — also what `selectedId` compares against for `aria-current`. */
  id: string | number;
  /** The row's own primary label, e.g. "v3". */
  label: string;
  status: VersionPanelStatus;
  /** Caller-resolved display text for `status` (report/template vocab differ). */
  statusLabel: string;
  /** "Current" badge — independent of `status` (a draft can be the current version). */
  isCurrent?: boolean;
  /** Freeform secondary lines: title, generated/approved stamps, artifact state. */
  meta?: ReactNode[];
  /** Why a locked/immutable row can't change, stated ON the row — never just a disabled control. */
  lockedReason?: ReactNode;
  /** The row's own action (e.g. a "View"/"Viewing" button) — caller-supplied, per the table's own "plus caller-supplied actions". */
  action?: ReactNode;
}

export interface VersionPanelProps {
  /** Both the visible heading text and the region's accessible name (via `aria-labelledby`, never a duplicate `aria-label`). */
  title: string;
  headingLevel?: 2 | 3 | 4;
  hint?: ReactNode;
  rows: VersionPanelRow[];
  selectedId?: string | number;
  /** Optional trailing header action — same convention `RailPanel.action` already uses. */
  action?: ReactNode;
  emptyLabel?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function VersionPanel({
  title,
  headingLevel = 3,
  hint,
  rows,
  selectedId,
  action,
  emptyLabel,
  className = '',
  'data-testid': testId,
}: VersionPanelProps): JSX.Element {
  const headingId = useId();
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';

  return (
    <section
      className={`rounded-hairline border border-border bg-card p-4 shadow-sm ${className}`}
      aria-labelledby={headingId}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <Heading id={headingId} className="min-w-0 text-sm font-semibold text-foreground">
          {title}
        </Heading>
        {action && <div className="shrink-0">{action}</div>}
      </div>

      {hint && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}

      {rows.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">{emptyLabel ?? 'Nothing here yet.'}</p>
      ) : (
        <ol className="mt-4 space-y-3">
          {rows.map((row) => {
            const isSelected = row.id === selectedId;
            const tone = STATUS_TONE[row.status];
            const weight = STATUS_WEIGHT[row.status];
            return (
              <li
                key={row.id}
                // `aria-current` (not a colour) is what tells assistive tech which
                // row is selected — same rule `VersionHistory` established.
                {...(isSelected ? { 'aria-current': 'true' as const } : {})}
                className={`rounded-xl border p-3 ${
                  isSelected ? 'border-primary bg-primary/[0.06]' : 'border-border'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{row.label}</span>
                    <StatusChip tone={tone} label={row.statusLabel} value={row.status} {...(weight ? { weight } : {})} />
                    {row.isCurrent && <Badge tone="neutral">Current</Badge>}
                  </div>
                  {row.action}
                </div>
                {row.meta?.map((line, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- meta is a
                  // caller-supplied, order-stable list of freeform lines, not a
                  // keyed collection with its own identity.
                  <p key={i} className="mt-1 text-xs text-muted-foreground">
                    {line}
                  </p>
                ))}
                {row.lockedReason && <p className="mt-1 text-xs text-muted-foreground">{row.lockedReason}</p>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
